/**
 * /pilot-qualification — structured qualification form that drives the
 * HOT / WARM / REJECT engine on the backend (POST /api/pilots/qualify).
 *
 * HOT leads receive a Lemon Squeezy checkout URL in the response and by email;
 * WARM leads are told we'll contact them; REJECT leads get a polite close.
 */
import { useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Rocket, Loader2, ArrowLeft, CheckCircle2, Clock, XCircle, ExternalLink, Shield,
} from 'lucide-react';
import { toast } from 'sonner';
import Logo from '@/components/Logo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INDUSTRIES = [
  { value: 'steel',       label: 'Steel' },
  { value: 'cement',      label: 'Cement' },
  { value: 'aluminium',   label: 'Aluminium' },
  { value: 'fertilizers', label: 'Fertilizers' },
  { value: 'chemicals',   label: 'Chemicals' },
  { value: 'other',       label: 'Other' },
];

const TIMELINES = [
  { value: 'immediate',       label: 'Immediate (within 2 weeks)' },
  { value: 'within_month',    label: 'Within a month' },
  { value: 'within_quarter',  label: 'Within 3 months' },
  { value: 'exploring',       label: 'Just exploring' },
];

const BUDGETS = [
  { value: 'available',   label: 'Yes, budget is allocated' },
  { value: 'requestable', label: 'No, but I can request it' },
  { value: 'unknown',     label: 'Not sure / no budget' },
];

const INITIAL = {
  company_name: '',
  vat_id: '',
  country: '',
  industry: 'steel',
  monthly_production_tons: '',
  products_count: '',
  has_exec_sponsor: '',
  has_daily_owner: '',
  timeline: '',
  budget_state: '',
  contact_name: '',
  contact_email: '',
  contact_role: '',
};

export default function PilotQualification() {
  const navigate = useNavigate();
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { lead_type, checkout_url, message, ls_configured }

  const set = (k) => (eOrV) => {
    const v = eOrV?.target ? eOrV.target.value : eOrV;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.contact_email.trim());
  const canSubmit =
    form.company_name.trim().length >= 2 &&
    !!form.industry &&
    Number(form.monthly_production_tons) > 0 &&
    form.has_exec_sponsor !== '' &&
    form.has_daily_owner !== '' &&
    !!form.timeline &&
    !!form.budget_state &&
    form.contact_name.trim().length >= 2 &&
    emailValid;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      toast.error('Please fill every required field with a valid value.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/pilots/qualify`, {
        company_name: form.company_name.trim(),
        vat_id: form.vat_id.trim(),
        country: form.country.trim(),
        industry: form.industry,
        monthly_production_tons: Number(form.monthly_production_tons),
        products_count: form.products_count ? Number(form.products_count) : null,
        has_exec_sponsor: form.has_exec_sponsor === 'yes',
        has_daily_owner: form.has_daily_owner === 'yes',
        timeline: form.timeline,
        budget_state: form.budget_state,
        contact_name: form.contact_name.trim(),
        contact_email: form.contact_email.trim().toLowerCase(),
        contact_role: form.contact_role.trim(),
      });
      setResult(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Qualification submission failed.');
    } finally {
      setSubmitting(false);
    }
  };

  // --- Result screen ---------------------------------------------------------
  if (result) {
    const t = result.lead_type;
    const meta = {
      hot:    { Icon: CheckCircle2, color: 'text-green-600',  bg: 'bg-green-50',  border: 'border-green-200',  title: "You're approved" },
      warm:   { Icon: Clock,        color: 'text-amber-600',  bg: 'bg-amber-50',  border: 'border-amber-200',  title: "We'd like to talk" },
      reject: { Icon: XCircle,      color: 'text-slate-500',  bg: 'bg-slate-50',  border: 'border-slate-200',  title: 'Thanks for your interest' },
    }[t];
    const { Icon } = meta;
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid={`qualification-result-${t}`}>
        <Card className={`max-w-xl w-full p-10 ${meta.border}`}>
          <div className={`w-14 h-14 rounded-2xl ${meta.bg} flex items-center justify-center mb-5`}>
            <Icon className={`w-7 h-7 ${meta.color}`} />
          </div>
          <h1 className="text-2xl font-heading font-bold mb-2" data-testid="qualification-result-title">
            {meta.title}
          </h1>
          <p className="text-sm text-muted-foreground mb-6" data-testid="qualification-result-message">
            {result.message}
          </p>

          {t === 'hot' && result.checkout_url && (
            <a href={result.checkout_url}
               target="_blank"
               rel="noopener noreferrer"
               data-testid="qualification-checkout-link">
              <Button className="w-full bg-primary hover:bg-primary/90">
                <ExternalLink className="w-4 h-4 mr-2" /> Pay €5,000 &amp; start the pilot
              </Button>
            </a>
          )}

          {t === 'hot' && !result.checkout_url && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              Our billing team will email your secure payment link shortly.
              (Payment provider is being provisioned — no action needed from you.)
            </div>
          )}

          <div className="mt-6 flex items-center justify-between text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5"><Shield className="w-3.5 h-3.5" /> EU-VAT handled by Lemon Squeezy</span>
            <Button variant="ghost" size="sm" onClick={() => navigate('/pricing')} data-testid="qualification-back-btn">
              <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Pricing
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // --- Form screen -----------------------------------------------------------
  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="pilot-qualification-page">
      <header className="border-b border-border bg-card/60 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-4 py-4">
          <Link to="/"><Logo size={30} variant="full" /></Link>
          <Link to="/pricing" className="text-sm text-muted-foreground hover:text-primary transition-colors" data-testid="pq-back-pricing">
            &larr; Back to pricing
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-10">
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold mb-3">
            <Rocket className="w-3.5 h-3.5" /> CBAM Readiness Pilot — Qualification
          </div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight">
            See if our 8‑week pilot fits your company
          </h1>
          <p className="mt-3 text-muted-foreground text-sm">
            Takes ~3 minutes. If you qualify, you'll receive the secure €5,000 payment link
            instantly. Otherwise, a Veridex specialist will guide you to the right next step.
          </p>
        </div>

        <Card className="p-6 sm:p-8">
          <form className="space-y-6" onSubmit={submit} data-testid="pilot-qualification-form">
            {/* Company */}
            <div>
              <h2 className="text-sm font-heading font-semibold mb-3 text-primary">1 · Company</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="q-company">Company name *</Label>
                  <Input id="q-company" required value={form.company_name} onChange={set('company_name')}
                         data-testid="q-company-input" placeholder="Acme Steel GmbH" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="q-vat">VAT ID (EU preferred)</Label>
                  <Input id="q-vat" value={form.vat_id} onChange={set('vat_id')}
                         data-testid="q-vat-input" placeholder="DE123456789" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="q-country">Country of production</Label>
                  <Input id="q-country" value={form.country} onChange={set('country')}
                         data-testid="q-country-input" placeholder="Germany" />
                </div>
                <div className="space-y-1.5">
                  <Label>Industry *</Label>
                  <Select value={form.industry} onValueChange={set('industry')}>
                    <SelectTrigger data-testid="q-industry-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {INDUSTRIES.map((i) => (
                        <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Operations */}
            <div>
              <h2 className="text-sm font-heading font-semibold mb-3 text-primary">2 · Operations</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="q-monthly">Monthly production exported to EU (tons) *</Label>
                  <Input id="q-monthly" type="number" min="0" step="1" required
                         value={form.monthly_production_tons}
                         onChange={set('monthly_production_tons')}
                         data-testid="q-monthly-input" placeholder="e.g. 500" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="q-products"># products in pilot scope</Label>
                  <Input id="q-products" type="number" min="0" step="1"
                         value={form.products_count}
                         onChange={set('products_count')}
                         data-testid="q-products-input" placeholder="1-10" />
                </div>
              </div>
            </div>

            {/* Readiness */}
            <div>
              <h2 className="text-sm font-heading font-semibold mb-3 text-primary">3 · Internal Readiness</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Executive sponsor available? *</Label>
                  <Select value={form.has_exec_sponsor} onValueChange={set('has_exec_sponsor')}>
                    <SelectTrigger data-testid="q-exec-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes (CFO / CSO / Compliance Lead)</SelectItem>
                      <SelectItem value="no">No — not yet identified</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Daily owner (4-6 h/wk)? *</Label>
                  <Select value={form.has_daily_owner} onValueChange={set('has_daily_owner')}>
                    <SelectTrigger data-testid="q-owner-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes, I can assign someone</SelectItem>
                      <SelectItem value="no">No / unsure</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Decision timeline *</Label>
                  <Select value={form.timeline} onValueChange={set('timeline')}>
                    <SelectTrigger data-testid="q-timeline-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      {TIMELINES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Budget state *</Label>
                  <Select value={form.budget_state} onValueChange={set('budget_state')}>
                    <SelectTrigger data-testid="q-budget-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                    <SelectContent>
                      {BUDGETS.map((b) => (
                        <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Contact */}
            <div>
              <h2 className="text-sm font-heading font-semibold mb-3 text-primary">4 · Contact</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="q-name">Your name *</Label>
                  <Input id="q-name" required value={form.contact_name} onChange={set('contact_name')}
                         data-testid="q-name-input" placeholder="Jane Doe" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="q-role">Your role</Label>
                  <Input id="q-role" value={form.contact_role} onChange={set('contact_role')}
                         data-testid="q-role-input" placeholder="CFO / Head of Sustainability" />
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="q-email">Work email *</Label>
                  <Input id="q-email" type="email" required value={form.contact_email} onChange={set('contact_email')}
                         data-testid="q-email-input" placeholder="jane@acme.com" />
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-4 border-t border-border">
              <p className="text-xs text-muted-foreground">
                By submitting you agree to our{' '}
                <Link to="/legal/privacy" className="text-primary hover:underline">Privacy Policy</Link>{' '}
                and{' '}
                <Link to="/legal/terms" className="text-primary hover:underline">Terms</Link>.
                This pilot is a structured readiness engagement and does not constitute a regulatory certification.
              </p>
              <Button type="submit" disabled={!canSubmit || submitting} className="bg-primary hover:bg-primary/90 whitespace-nowrap" data-testid="q-submit-btn">
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Rocket className="w-4 h-4 mr-2" />}
                {submitting ? 'Checking eligibility…' : 'Check eligibility'}
              </Button>
            </div>
          </form>
        </Card>
      </main>
    </div>
  );
}
